import http, { handleApiResponse, handleApiResponseWithSuccess } from "@/services/http";
import ApiResponse from "@/models/ApiResponse";
import eventBus from "@/services/eventBus";
import dayjs from "dayjs";

import MetRiportHead from "../models/MetRiportHead";
import RiportStatus from "../models/RiportStatus";
import MetRiportItem from "../models/MetRiportItem";
import ItemType from "../models/ItemType";
import ExportStatus from "../models/ExportStatus";
import MetRiportExportHead from "../models/MetRiportExportHead";
import MetRiportExportItem from "../models/MetRiportExportItem";

export default class MetRiportApi {

    //---------- Riport ----------//

    static getMetRiports(): Promise<MetRiportHead[]> {
        return http.get<ApiResponse<MetRiportHead[]>>(`met-riport/riports`)
            .then(handleApiResponse);
    }

    static removeMetRiport(id: number): Promise<void> {
        return http.delete<ApiResponse<void>>(`met-riport/riport/remove/${id}`)
            .then(handleApiResponseWithSuccess);
    }

    static copyMetRiport(id: number): Promise<void> {
        return http.post<ApiResponse<void>>(`met-riport/riport/copy/${id}`)
            .then((data) => handleApiResponseWithSuccess(data, `Sikeres riport sablon másolás`));
    }

    //---------- Riport fejléc ----------//

    static getMetRiportHead(id: number): Promise<MetRiportHead> {
        return http.get<ApiResponse<MetRiportHead>>(`met-riport/head/${id}`)
            .then(handleApiResponse);
    }
    
    static updateMetRiportHead(metRiportHead: MetRiportHead): Promise<number> {
        return http.post<ApiResponse<number>>(`met-riport/head/update`, { metRiportHead: metRiportHead })
            .then(handleApiResponseWithSuccess);
    }

    //---------- Riport tételek ----------//

    static updateMetRiportItem(metRiportItem: MetRiportItem): Promise<void> {
        return http.post<ApiResponse<void>>(`met-riport/item/update`, { metRiportItem: metRiportItem })
            .then(handleApiResponseWithSuccess);
    }

    static removeMetRiportItem(id: number): Promise<void> {
        return http.delete<ApiResponse<void>>(`met-riport/item/remove/${id}`)
            .then(handleApiResponseWithSuccess);
    }

    //---------- Export ----------//

    static removeMetRiportExport(id: number): Promise<void> {
        return http.delete<ApiResponse<void>>(`met-riport/export/remove/${id}`)
            .then(handleApiResponseWithSuccess);
    }

    static closeMetRiportExport(id: number): Promise<void> {
        return http.post<ApiResponse<void>>(`met-riport/export/close/${id}`)
            .then(handleApiResponseWithSuccess);
    }

    static exportMetRiportToCSV(metRiportExportHead: MetRiportExportHead) {
        return http.post(`met-riport/riport/export-csv`, { metRiportExportHead: metRiportExportHead }, { responseType: 'blob' })
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
        
                const link = document.createElement('a');
                link.href = url;
                link.download = `MetRip_${metRiportExportHead.metisHeadName}_${dayjs(metRiportExportHead.beginDate).format('YYYY.MM.DD')}-${dayjs(metRiportExportHead.endDate).format('YYYY.MM.DD')}.zip`;
                link.click();
                window.URL.revokeObjectURL(url);
                eventBus.$emit("success", "Sikeres exportálás");
            })
            .catch(error => {
                eventBus.$emit("error", error);
                console.error(error);
            });
    }

    //---------- Export fejléc ----------//

    static getMetRiportExportHead(id: number): Promise<MetRiportExportHead> {
        return http.get<ApiResponse<MetRiportExportHead>>(`met-riport/export/head/${id}`)
            .then(handleApiResponse);
    }

    static updateMetRiportExportHead(metRiportExportHead: MetRiportExportHead): Promise<number> {
        return http.post<ApiResponse<number>>(`met-riport/export/head/update`, { metRiportExportHead: metRiportExportHead })
            .then(handleApiResponseWithSuccess);
    }

    //---------- Export tételek ----------//

    static updateMetRiportExportItem(metRiportExportItem: MetRiportExportItem): Promise<void> {
        return http.post<ApiResponse<void>>(`met-riport/export/item/update`, { metRiportExportItem: metRiportExportItem })
            .then(handleApiResponseWithSuccess);
    }

    static importMetRiportExportItems(id: number, file: File | undefined): Promise<string> {
        const importData = new FormData()
        importData.append("id", id.toString());
        importData.append("file", file ?? "");

        return http.post<ApiResponse<string>>(`met-riport/export/items/import`, importData, { headers: { "Content-Type": "multipart/form-data" }})
            .then((data) => handleApiResponseWithSuccess(data, data.data.data == "" ? "" : `Sikeres mentés. A következő tételek nem szerepelnek a tétellistába: ${data.data.data}` ));
    }

    static updateMetRiportExportItemsValues(metRiportExportHead: MetRiportExportHead): Promise<void> {
        return http.post<ApiResponse<void>>(`met-riport/export/values/generate`, { metRiportExportHead: metRiportExportHead })
            .then(handleApiResponse);
    }

    //---------- Egyéb ----------//

    static getRiportStatuses(): Promise<RiportStatus[]> {
        return http.get<ApiResponse<RiportStatus[]>>(`met-riport/supplycomponents/statuses`)
            .then(handleApiResponse);
    }

    static getItemTypes(): Promise<ItemType[]> {
        return http.get<ApiResponse<ItemType[]>>(`met-riport/supplycomponents/itemtypes`)
            .then(handleApiResponse);
    }

    static getExportStatuses(): Promise<ExportStatus[]> {
        return http.get<ApiResponse<ExportStatus[]>>(`met-riport/supplycomponents/exportstatuses`)
            .then(handleApiResponse);
    }
}